import { useNavigate } from "react-router-dom";
import { LayoutGrid, PlusCircle } from "lucide-react";
import AnimatedBackground from "../components/assets/backgrounds/AnimatedBackground";
import NewsPanel from "../components/home/NewsPanel";
import { useAuth } from "../context/AuthContext";
import { dictionary, Language } from "../utils/dictionary";
import { Tooltip } from "../components/utils/Tooltip/Tooltip";

const locales: Record<Language, string> = {
  [Language.SPANISH]: "es-ES",
  [Language.ENGLISH]: "en-US",
  [Language.PORTUGUESE]: "pt-BR",
};

const getFormattedDate = (language: Language): string => {
  const d = new Date().toLocaleDateString(locales[language], {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return d.charAt(0).toUpperCase() + d.slice(1);
};

const isOnboardingDisabled =
  import.meta.env.VITE_DISABLED_ONBOARDING === "true";

const HomePage = () => {
  const navigate = useNavigate();
  const { language } = useAuth();

  const screenWidth = window.innerWidth;
  if (screenWidth < 768) {
    navigate("/settings");
    return null;
  }

  const formattedDate = getFormattedDate(language as Language);

  return (
    <div className="h-screen font-['Plus_Jakarta_Sans'] flex flex-col overflow-hidden">
      <AnimatedBackground />

      <main className="relative z-10 flex-1 flex justify-center overflow-hidden">
        <div
            className="w-full flex gap-8 pl-4 pr-10 h-full"
            style={{ maxWidth: "clamp(1100px, 78vw, 1500px)" }}
          >
          {/* Columna izquierda */}
          <div className="flex-1 flex flex-col min-w-0 h-full">
            {/* Contenido superior — entre centro y mascota */}
            <div className="flex-1 flex flex-col justify-center">
              {/* Fecha */}
              <p
                className="font-medium tracking-wide text-[hsl(230,10%,46%)]"
                style={{ fontSize: "clamp(1rem, 1.2vw, 1.5rem)" }}
              >
                {formattedDate}
              </p>

              {/* Titulo */}
              <h1
                className="font-extrabold leading-tight mt-1.5 text-[hsl(230,25%,12%)]"
                style={{ fontSize: "clamp(2.5rem, 3.2vw, 4rem)" }}
              >
                {dictionary["question-home"][language as Language]}
              </h1>

              {/* Botones de accion */}
              <div className="flex gap-4 mt-8">
                {/* Ver mis embudos — outline */}
                <button
                  onClick={() => navigate("/funnels")}
                  className="flex items-center gap-3 rounded-full border bg-white font-semibold transition-all duration-200 cursor-pointer hover:shadow-md"
                  style={{
                    fontSize: "clamp(0.875rem, 1vw, 1.125rem)",
                    padding: "clamp(0.75rem, 0.9vw, 1.1rem) clamp(1.5rem, 1.8vw, 2rem)",
                    borderColor: "hsl(230 20% 88%)",
                    color: "hsl(230 25% 12%)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor =
                      "hsl(226 76% 52% / 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "hsl(230 20% 88%)";
                  }}
                >
                  <LayoutGrid className="w-5 h-5 text-[hsl(230,10%,46%)]" />
                  {dictionary["see-my-funnels"][language as Language]}
                </button>

                {/* Crear nuevo embudo — gradient */}
                {isOnboardingDisabled ? (
                  <Tooltip
                    content={
                      dictionary[
                        "problems-with-the-creation-of-pages-and-images"
                      ]?.[language] ??
                      "La opcion de crear esta deshabilitada"
                    }
                  >
                    <div>
                      <button
                        disabled
                        className="flex items-center gap-3 rounded-full font-semibold text-white opacity-50 cursor-not-allowed"
                        style={{
                          fontSize: "clamp(0.875rem, 1.1vw, 1.25rem)",
                          padding: "clamp(0.75rem, 1vw, 1.25rem) clamp(1.5rem, 2vw, 2.25rem)",
                          background:
                            "linear-gradient(135deg, hsl(226 76% 58%), hsl(218 70% 52%))",
                        }}
                      >
                        <PlusCircle className="w-5 h-5" />
                        {dictionary["create-new-funnel"][language as Language]}
                      </button>
                    </div>
                  </Tooltip>
                ) : (
                  <button
                    id="createSalesPage-target"
                    onClick={() => navigate("/onboarding")}
                    className="flex items-center gap-3 rounded-full font-semibold text-white transition-all duration-200 cursor-pointer hover:scale-[1.02]"
                    style={{
                      fontSize: "clamp(0.875rem, 1.1vw, 1.25rem)",
                      padding: "clamp(0.75rem, 1vw, 1.25rem) clamp(1.5rem, 2vw, 2.25rem)",
                      background:
                        "linear-gradient(135deg, hsl(226 76% 58%), hsl(218 70% 52%))",
                      boxShadow:
                        "0 10px 15px -3px hsl(226 76% 52% / 0.25)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 20px 25px -5px hsl(226 76% 52% / 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow =
                        "0 10px 15px -3px hsl(226 76% 52% / 0.25)";
                    }}
                  >
                    <PlusCircle className="w-5 h-5" />
                    {dictionary["create-new-funnel"][language as Language]}
                  </button>
                )}
              </div>
            </div>

            {/* Mascota — pegada al fondo, alineada a la izquierda */}
            <div className="shrink-0 flex justify-start">
              <img
                src="https://fluxi.co/prod/assets/images/portada-fluxi-v3.webp"
                alt="Fluxi Mascot"
                className="w-auto object-contain object-bottom pointer-events-none select-none"
                style={{ height: "clamp(420px, 35vh, 650px)", transform: "scale(1.1)", transformOrigin: "bottom left" }}
              />
            </div>
          </div>

        </div>
      </main>

      {/* Panel de noticias — flotante a la derecha, no afecta el layout */}
      <div className="hidden md:flex items-center fixed top-0 right-[180px] bottom-0 z-30 -mt-8">
        <NewsPanel language={language as Language} />
      </div>
    </div>
  );
};

export default HomePage;
